import { useParams, useNavigate } from 'react-router-dom'
import {
  ArrowLeft, Users, DollarSign, FileText, CheckCircle, AlertCircle,
  Clock, Upload, X, Plus, MoreVertical, Download, Eye,
} from 'lucide-react'
import { useState, useEffect, useCallback } from 'react'
import { contractsService, type Contract } from '../services/contracts'
import { projectService, type Project as ApiProject, type TeamMember } from '../services/projects'
import { complianceService, type ComplianceCheck, type ComplianceScore } from '../services/compliance'
import { toast } from 'react-hot-toast'

function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean)
  if (parts.length === 0) return '?'
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase()
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
}

function formatMoney(value?: number | null): string {
  if (value == null || Number.isNaN(value)) return 'Not set'
  if (value >= 1_000_000) return `$${(value / 1_000_000).toFixed(1)}M`
  if (value >= 1_000) return `$${(value / 1_000).toFixed(0)}K`
  return `$${value.toLocaleString()}`
}

function daysRemaining(endDate?: string): string {
  if (!endDate) return '—'
  const end = new Date(endDate)
  if (Number.isNaN(end.getTime())) return '—'
  const diff = Math.ceil((end.getTime() - Date.now()) / (1000 * 60 * 60 * 24))
  if (diff < 0) return 'Ended'
  return String(diff)
}

function statusColor(status: string): string {
  switch (status.toLowerCase()) {
    case 'active':
    case 'executed':
    case 'compliant':
      return 'bg-green-100 text-green-800'
    case 'planning':
    case 'pending':
    case 'pending_approval':
      return 'bg-blue-100 text-blue-800'
    case 'under_review':
    case 'in_progress':
      return 'bg-yellow-100 text-yellow-800'
    case 'completed':
    case 'draft':
      return 'bg-gray-100 text-gray-800'
    default:
      return 'bg-gray-100 text-gray-800'
  }
}

function complianceTone(score: number | null): string {
  if (score == null) return 'text-gray-400'
  if (score >= 90) return 'text-green-600'
  if (score >= 75) return 'text-yellow-600'
  return 'text-red-600'
}

export default function ProjectDetailPage() {
  const { projectId } = useParams<{ projectId: string }>()
  const navigate = useNavigate()
  const [project, setProject] = useState<ApiProject | null>(null)
  const [contracts, setContracts] = useState<Contract[]>([])
  const [team, setTeam] = useState<TeamMember[]>([])
  const [checks, setChecks] = useState<ComplianceCheck[]>([])
  const [score, setScore] = useState<ComplianceScore | null>(null)
  const [activeTab, setActiveTab] = useState('overview')
  const [loading, setLoading] = useState(false)
  const [showUploadModal, setShowUploadModal] = useState(false)
  const [showComplianceModal, setShowComplianceModal] = useState(false)
  const [showReportModal, setShowReportModal] = useState(false)
  const [showInviteModal, setShowInviteModal] = useState(false)
  const [uploadFile, setUploadFile] = useState<File | null>(null)
  const [inviteEmail, setInviteEmail] = useState('')
  const [inviteRole, setInviteRole] = useState('team_member')
  const [contractDropdownOpen, setContractDropdownOpen] = useState<string | null>(null)
  const [complianceOptions, setComplianceOptions] = useState({
    checkContracts: true,
    checkRegulations: true,
    generateReport: false,
  })

  const load = useCallback(async () => {
    if (!projectId) return
    try {
      const data = await projectService.getProject(projectId)
      setProject(data)
    } catch {
      toast.error('Failed to load project details')
      navigate('/projects')
      return
    }

    const [contractResult, teamResult, checkResult, scoreResult] = await Promise.allSettled([
      contractsService.getProjectContracts(projectId),
      projectService.getTeam(projectId),
      complianceService.getComplianceChecks(projectId),
      complianceService.getComplianceScore(projectId),
    ])

    setContracts(contractResult.status === 'fulfilled' ? contractResult.value : [])
    setTeam(teamResult.status === 'fulfilled' ? teamResult.value : [])
    setChecks(checkResult.status === 'fulfilled' ? checkResult.value : [])
    setScore(scoreResult.status === 'fulfilled' ? scoreResult.value : null)
  }, [projectId, navigate])

  useEffect(() => {
    load()
  }, [load])

  useEffect(() => {
    const handleClickOutside = () => {
      if (contractDropdownOpen) setContractDropdownOpen(null)
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [contractDropdownOpen])

  const handleRunComplianceCheck = async () => {
    if (!projectId) return
    try {
      setLoading(true)
      await complianceService.checkProjectCompliance(projectId)
      setShowComplianceModal(false)
      toast.success('Compliance check initiated successfully!')
      await load()
    } catch {
      toast.error('Failed to run compliance check')
    } finally {
      setLoading(false)
    }
  }

  const handleGenerateReport = async () => {
    if (!projectId) return
    try {
      setLoading(true)
      const report = await complianceService.checkProjectCompliance(projectId)
      const content = [
        'Project Compliance Report',
        `Project: ${project?.name ?? 'Unknown'}`,
        `Generated: ${new Date().toLocaleString()}`,
        `Compliance Score: ${report.compliance_score}%`,
        `Total Checks: ${report.total_checks}`,
      ].join('\n')
      const blob = new Blob([content], { type: 'text/plain' })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `compliance-report-${projectId}.txt`
      a.click()
      URL.revokeObjectURL(url)
      setShowReportModal(false)
      toast.success('Report downloaded')
    } catch {
      toast.error('Failed to generate report')
    } finally {
      setLoading(false)
    }
  }

  const handleSendInvite = async () => {
    if (!projectId || !inviteEmail) {
      toast.error('Please enter an email address')
      return
    }
    try {
      setLoading(true)
      await projectService.inviteMember(projectId, inviteEmail, inviteRole)
      toast.success('Team member added')
      setShowInviteModal(false)
      setInviteEmail('')
      await load()
    } catch (error: any) {
      toast.error(error.response?.data || error.response?.data?.message || 'Could not add that user. They must already have an account in your organization.')
    } finally {
      setLoading(false)
    }
  }

  if (!project) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">Loading project details...</div>
      </div>
    )
  }

  const location = [project.address, project.city, project.state, project.zipcode]
    .filter(Boolean)
    .join(', ')
  const overallScore = score?.overall_score ?? null
  const executed = contracts.filter((c) => ['active', 'executed'].includes((c.status || '').toLowerCase())).length
  const underReview = contracts.filter((c) => (c.status || '').toLowerCase().includes('review')).length
  const pending = contracts.filter((c) => (c.status || '').toLowerCase().includes('pending') || c.status === 'draft').length

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="mb-8">
        <button
          onClick={() => navigate('/projects')}
          className="flex items-center text-gray-600 hover:text-gray-900 mb-4"
        >
          <ArrowLeft className="h-5 w-5 mr-2" />
          Back to Projects
        </button>

        <div className="bg-white shadow rounded-lg p-6">
          <div className="flex justify-between items-start">
            <div>
              <div className="flex items-center gap-3">
                <h1 className="text-3xl font-bold text-gray-900">{project.name}</h1>
                {project.is_sample && (
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800">
                    Sample
                  </span>
                )}
              </div>
              <p className="text-lg text-gray-600 mt-1">{project.client_name || project.description || 'No client listed'}</p>
              {project.is_sample && (
                <p className="text-sm text-indigo-700 mt-2">
                  This is a walkthrough project created for your organization. Real projects you add start empty.
                </p>
              )}
            </div>
            <div className="flex items-center space-x-4">
              <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${statusColor(project.status || 'active')}`}>
                {(project.status || 'active').charAt(0).toUpperCase() + (project.status || 'active').slice(1)}
              </span>
              <div className="text-center">
                <p className="text-sm text-gray-500">Compliance</p>
                <p className={`text-2xl font-bold ${complianceTone(overallScore)}`}>
                  {overallScore == null ? '—' : `${overallScore}%`}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="border-b border-gray-200 mb-6">
        <nav className="-mb-px flex space-x-8">
          {['overview', 'contracts', 'compliance', 'team'].map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === tab
                  ? 'border-indigo-500 text-indigo-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              {tab.charAt(0).toUpperCase() + tab.slice(1)}
            </button>
          ))}
        </nav>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {activeTab === 'overview' && (
          <>
            <div className="lg:col-span-2 space-y-6">
              <div className="bg-white shadow rounded-lg p-6">
                <h2 className="text-xl font-semibold mb-4">Project Information</h2>
                <dl className="grid grid-cols-1 gap-x-4 gap-y-6 sm:grid-cols-2">
                  <div>
                    <dt className="text-sm font-medium text-gray-500">Contract Value</dt>
                    <dd className="mt-1 text-lg font-semibold text-gray-900">{formatMoney(project.contract_value)}</dd>
                  </div>
                  <div>
                    <dt className="text-sm font-medium text-gray-500">Start Date</dt>
                    <dd className="mt-1 text-lg text-gray-900">{project.start_date || 'Not set'}</dd>
                  </div>
                  <div>
                    <dt className="text-sm font-medium text-gray-500">End Date</dt>
                    <dd className="mt-1 text-lg text-gray-900">{project.end_date || 'TBD'}</dd>
                  </div>
                  <div>
                    <dt className="text-sm font-medium text-gray-500">Location</dt>
                    <dd className="mt-1 text-lg text-gray-900">{location || 'Not specified'}</dd>
                  </div>
                  <div className="sm:col-span-2">
                    <dt className="text-sm font-medium text-gray-500">Description</dt>
                    <dd className="mt-1 text-gray-900">{project.description || 'No description yet'}</dd>
                  </div>
                </dl>
              </div>
            </div>

            <div className="space-y-6">
              <div className="bg-white shadow rounded-lg p-6">
                <h3 className="text-lg font-semibold mb-4">Quick Stats</h3>
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <FileText className="h-5 w-5 text-gray-400 mr-2" />
                      <span className="text-sm text-gray-600">Contracts</span>
                    </div>
                    <span className="text-sm font-semibold">{contracts.length}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <Users className="h-5 w-5 text-gray-400 mr-2" />
                      <span className="text-sm text-gray-600">Team Members</span>
                    </div>
                    <span className="text-sm font-semibold">{team.length}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <Clock className="h-5 w-5 text-gray-400 mr-2" />
                      <span className="text-sm text-gray-600">Days Remaining</span>
                    </div>
                    <span className="text-sm font-semibold">{daysRemaining(project.end_date)}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <DollarSign className="h-5 w-5 text-gray-400 mr-2" />
                      <span className="text-sm text-gray-600">Contract Value</span>
                    </div>
                    <span className="text-sm font-semibold">{formatMoney(project.contract_value)}</span>
                  </div>
                </div>
              </div>

              <div className="bg-white shadow rounded-lg p-6">
                <h3 className="text-lg font-semibold mb-4">Actions</h3>
                <div className="space-y-2">
                  <button
                    onClick={() => setShowUploadModal(true)}
                    className="w-full text-left px-4 py-2 text-sm font-medium text-indigo-600 bg-indigo-50 rounded-md hover:bg-indigo-100 flex items-center"
                  >
                    <Upload className="h-4 w-4 mr-2" />
                    Upload Contract
                  </button>
                  <button
                    onClick={() => setShowComplianceModal(true)}
                    className="w-full text-left px-4 py-2 text-sm font-medium text-green-600 bg-green-50 rounded-md hover:bg-green-100 flex items-center"
                  >
                    <CheckCircle className="h-4 w-4 mr-2" />
                    Run Compliance Check
                  </button>
                  <button
                    onClick={() => setShowReportModal(true)}
                    className="w-full text-left px-4 py-2 text-sm font-medium text-purple-600 bg-purple-50 rounded-md hover:bg-purple-100 flex items-center"
                  >
                    <FileText className="h-4 w-4 mr-2" />
                    Generate Report
                  </button>
                  <button
                    onClick={() => setShowInviteModal(true)}
                    className="w-full text-left px-4 py-2 text-sm font-medium text-indigo-600 bg-indigo-50 rounded-md hover:bg-indigo-100 flex items-center"
                  >
                    <Users className="h-4 w-4 mr-2" />
                    Invite Team Member
                  </button>
                </div>
              </div>
            </div>
          </>
        )}

        {activeTab === 'contracts' && (
          <div className="lg:col-span-3">
            <div className="bg-white shadow rounded-lg p-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-xl font-semibold">Project Contracts</h2>
                <button
                  onClick={() => setShowUploadModal(true)}
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700"
                >
                  <Upload className="h-4 w-4 mr-2" />
                  Upload Contract
                </button>
              </div>

              {contracts.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <FileText className="h-10 w-10 mx-auto mb-3 text-gray-300" />
                  <p className="font-medium text-gray-900">No contracts yet</p>
                  <p className="text-sm mt-1">Upload a contract to start analysis and compliance tracking.</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {contracts.map((contract) => (
                    <div key={contract.id} className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
                      <div className="flex items-center">
                        <FileText className="h-8 w-8 text-blue-500" />
                        <div className="ml-4">
                          <h3 className="text-sm font-medium text-gray-900">{contract.name}</h3>
                          <p className="text-sm text-gray-500">
                            {contract.contract_type}
                            {contract.created_at ? ` · ${new Date(contract.created_at).toLocaleDateString()}` : ''}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center space-x-2">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${statusColor(contract.status)}`}>
                          {contract.status.replace('_', ' ')}
                        </span>
                        <div className="relative">
                          <button
                            className="text-gray-400 hover:text-gray-600"
                            onClick={(e) => {
                              e.stopPropagation()
                              setContractDropdownOpen(contractDropdownOpen === contract.id ? null : contract.id)
                            }}
                          >
                            <MoreVertical className="h-4 w-4" />
                          </button>
                          {contractDropdownOpen === contract.id && (
                            <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg ring-1 ring-black ring-opacity-5 z-10">
                              <div className="py-1">
                                <button
                                  onClick={() => {
                                    setContractDropdownOpen(null)
                                    navigate('/contracts')
                                  }}
                                  className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                                >
                                  <Eye className="h-4 w-4 mr-3" />
                                  View Contract
                                </button>
                                <button
                                  onClick={() => {
                                    setContractDropdownOpen(null)
                                    toast('Download is available from the contract record once a file is uploaded.')
                                  }}
                                  className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                                >
                                  <Download className="h-4 w-4 mr-3" />
                                  Download
                                </button>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              <div className="mt-8 grid grid-cols-1 md:grid-cols-4 gap-4">
                <div className="bg-gray-50 rounded-lg p-4">
                  <p className="text-sm font-medium text-gray-900">Total Contracts</p>
                  <p className="mt-2 text-2xl font-semibold text-gray-900">{contracts.length}</p>
                </div>
                <div className="bg-green-50 rounded-lg p-4">
                  <p className="text-sm font-medium text-gray-900">Active / Executed</p>
                  <p className="mt-2 text-2xl font-semibold text-green-600">{executed}</p>
                </div>
                <div className="bg-yellow-50 rounded-lg p-4">
                  <p className="text-sm font-medium text-gray-900">Under Review</p>
                  <p className="mt-2 text-2xl font-semibold text-yellow-600">{underReview}</p>
                </div>
                <div className="bg-blue-50 rounded-lg p-4">
                  <p className="text-sm font-medium text-gray-900">Pending / Draft</p>
                  <p className="mt-2 text-2xl font-semibold text-blue-600">{pending}</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'compliance' && (
          <div className="lg:col-span-3">
            <div className="bg-white shadow rounded-lg p-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-xl font-semibold">Compliance Status</h2>
                <button
                  onClick={() => setShowComplianceModal(true)}
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700"
                >
                  <CheckCircle className="h-4 w-4 mr-2" />
                  Run Check
                </button>
              </div>

              <div className="mb-8 p-6 bg-gray-50 rounded-lg">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-lg font-medium text-gray-900">Overall Compliance Score</h3>
                    <p className="text-sm text-gray-600">
                      {checks.length === 0 ? 'No checks have been run for this project.' : `${checks.length} checks on file`}
                    </p>
                  </div>
                  <div className="text-right">
                    <div className={`text-3xl font-bold ${complianceTone(overallScore)}`}>
                      {overallScore == null ? '—' : `${overallScore}%`}
                    </div>
                  </div>
                </div>
                {overallScore != null && (
                  <div className="mt-4 bg-white rounded-full h-2">
                    <div className="bg-green-500 h-2 rounded-full" style={{ width: `${Math.min(overallScore, 100)}%` }} />
                  </div>
                )}
              </div>

              {checks.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <AlertCircle className="h-10 w-10 mx-auto mb-3 text-gray-300" />
                  <p className="font-medium text-gray-900">No compliance activity yet</p>
                  <p className="text-sm mt-1">Run a check after you upload contracts.</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {checks.map((check) => (
                    <div key={check.id} className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
                      <div>
                        <h3 className="text-sm font-medium text-gray-900">{check.check_type.replace('_', ' ')}</h3>
                        <p className="text-sm text-gray-500">{check.notes || 'No notes'}</p>
                      </div>
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${statusColor(check.status)}`}>
                        {check.status.replace('_', ' ')}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {activeTab === 'team' && (
          <div className="lg:col-span-3">
            <div className="bg-white shadow rounded-lg p-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-xl font-semibold">Team Members</h2>
                <button
                  onClick={() => setShowInviteModal(true)}
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Invite Member
                </button>
              </div>

              {team.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <Users className="h-10 w-10 mx-auto mb-3 text-gray-300" />
                  <p className="font-medium text-gray-900">No team members on this project</p>
                  <p className="text-sm mt-1">Invite people who already have an account in your organization.</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {team.map((member) => {
                    const name = [member.first_name, member.last_name].filter(Boolean).join(' ') || member.email
                    return (
                      <div key={member.id} className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
                        <div className="flex items-center">
                          <div className="h-10 w-10 bg-indigo-500 rounded-full flex items-center justify-center">
                            <span className="text-sm font-medium text-white">{initials(name)}</span>
                          </div>
                          <div className="ml-4">
                            <h3 className="text-sm font-medium text-gray-900">{name}</h3>
                            <p className="text-sm text-gray-500">{member.role.replace('_', ' ')}</p>
                            <p className="text-xs text-gray-400">{member.email}</p>
                          </div>
                        </div>
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${statusColor(member.status)}`}>
                          {member.status}
                        </span>
                      </div>
                    )
                  })}
                </div>
              )}

              <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="bg-gray-50 rounded-lg p-4">
                  <p className="text-sm font-medium text-gray-900">Total Members</p>
                  <p className="mt-2 text-2xl font-semibold">{team.length}</p>
                </div>
                <div className="bg-green-50 rounded-lg p-4">
                  <p className="text-sm font-medium text-gray-900">Active</p>
                  <p className="mt-2 text-2xl font-semibold text-green-600">
                    {team.filter((m) => m.status === 'active').length}
                  </p>
                </div>
                <div className="bg-yellow-50 rounded-lg p-4">
                  <p className="text-sm font-medium text-gray-900">Pending</p>
                  <p className="mt-2 text-2xl font-semibold text-yellow-600">
                    {team.filter((m) => m.status !== 'active').length}
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {showUploadModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-medium text-gray-900">Upload Contract</h3>
              <button onClick={() => setShowUploadModal(false)} className="text-gray-400 hover:text-gray-600">
                <X className="h-6 w-6" />
              </button>
            </div>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">Contract Name</label>
              <input
                id="contractName"
                type="text"
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
                placeholder="e.g., Main Construction Agreement"
                defaultValue={uploadFile?.name.replace(/\.[^/.]+$/, '') || ''}
              />
            </div>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">Select Contract File</label>
              <input
                type="file"
                accept=".pdf,.doc,.docx"
                onChange={(e) => setUploadFile(e.target.files?.[0] || null)}
                className="block w-full text-sm text-gray-500"
              />
            </div>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">Contract Type</label>
              <select id="contractType" className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2">
                <option value="Construction">Main Contract</option>
                <option value="Amendment">Amendment</option>
                <option value="Subcontract">Subcontract</option>
                <option value="Purchase Order">Purchase Order</option>
              </select>
            </div>
            <div className="flex justify-end space-x-3">
              <button onClick={() => setShowUploadModal(false)} className="px-4 py-2 text-sm bg-gray-100 rounded-md">
                Cancel
              </button>
              <button
                disabled={!uploadFile}
                onClick={async () => {
                  if (!uploadFile || !projectId) return
                  const contractName = (document.getElementById('contractName') as HTMLInputElement)?.value || uploadFile.name
                  const contractType = (document.getElementById('contractType') as HTMLSelectElement)?.value || 'Construction'
                  try {
                    const loadingToast = toast.loading('Creating contract...')
                    const created = await contractsService.createContract({
                      project_id: projectId,
                      name: contractName,
                      contract_type: contractType,
                      party_a_name: project.client_name || 'Client',
                      party_a_type: 'Owner',
                      party_b_name: 'Contractor',
                      party_b_type: 'General Contractor',
                      start_date: project.start_date,
                      end_date: project.end_date,
                    })
                    await contractsService.uploadContract(created.id, uploadFile)
                    toast.dismiss(loadingToast)
                    toast.success('Contract uploaded')
                    setShowUploadModal(false)
                    setUploadFile(null)
                    await load()
                  } catch (error: any) {
                    toast.error(error.response?.data?.detail || error.message || 'Failed to upload contract')
                  }
                }}
                className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-md disabled:bg-gray-300"
              >
                Upload
              </button>
            </div>
          </div>
        </div>
      )}

      {showComplianceModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-medium text-gray-900">Run Compliance Check</h3>
              <button onClick={() => setShowComplianceModal(false)} className="text-gray-400 hover:text-gray-600">
                <X className="h-6 w-6" />
              </button>
            </div>
            <p className="text-sm text-gray-600 mb-4">
              This runs a compliance check on the contracts and records currently stored for this project.
            </p>
            <label className="flex items-center">
              <input
                type="checkbox"
                className="rounded"
                checked={complianceOptions.checkContracts}
                onChange={(e) => setComplianceOptions({ ...complianceOptions, checkContracts: e.target.checked })}
              />
              <span className="ml-2 text-sm text-gray-700">Check contract compliance</span>
            </label>
            <label className="flex items-center mt-2">
              <input
                type="checkbox"
                className="rounded"
                checked={complianceOptions.checkRegulations}
                onChange={(e) => setComplianceOptions({ ...complianceOptions, checkRegulations: e.target.checked })}
              />
              <span className="ml-2 text-sm text-gray-700">Verify regulatory requirements</span>
            </label>
            <label className="flex items-center mt-2 mb-4">
              <input
                type="checkbox"
                className="rounded"
                checked={complianceOptions.generateReport}
                onChange={(e) => setComplianceOptions({ ...complianceOptions, generateReport: e.target.checked })}
              />
              <span className="ml-2 text-sm text-gray-700">Generate detailed report</span>
            </label>
            <div className="flex justify-end space-x-3">
              <button onClick={() => setShowComplianceModal(false)} className="px-4 py-2 text-sm bg-gray-100 rounded-md" disabled={loading}>
                Cancel
              </button>
              <button
                onClick={handleRunComplianceCheck}
                disabled={loading}
                className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-md"
              >
                {loading ? 'Running...' : 'Run Check'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showReportModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-medium text-gray-900">Generate Report</h3>
              <button onClick={() => setShowReportModal(false)} className="text-gray-400 hover:text-gray-600">
                <X className="h-6 w-6" />
              </button>
            </div>
            <p className="text-sm text-gray-600 mb-4">Downloads a text report from the latest compliance data for this project.</p>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">Report Type</label>
              <select className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2">
                <option value="summary">Project Summary</option>
                <option value="compliance">Compliance Report</option>
              </select>
            </div>
            <div className="flex justify-end space-x-3">
              <button onClick={() => setShowReportModal(false)} className="px-4 py-2 text-sm bg-gray-100 rounded-md" disabled={loading}>
                Cancel
              </button>
              <button onClick={handleGenerateReport} disabled={loading} className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-md">
                {loading ? 'Generating...' : 'Generate'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showInviteModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-medium text-gray-900">Invite Team Member</h3>
              <button onClick={() => setShowInviteModal(false)} className="text-gray-400 hover:text-gray-600">
                <X className="h-6 w-6" />
              </button>
            </div>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">Email Address</label>
              <input
                type="email"
                placeholder="colleague@company.com"
                value={inviteEmail}
                onChange={(e) => setInviteEmail(e.target.value)}
                className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
              />
              <p className="mt-1 text-xs text-gray-500">They must already have an account in your organization.</p>
            </div>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">Role</label>
              <select
                className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                value={inviteRole}
                onChange={(e) => setInviteRole(e.target.value)}
              >
                <option value="team_member">Project Member</option>
                <option value="project_manager">Project Manager</option>
                <option value="contractor">Contractor</option>
                <option value="client_rep">Client Representative</option>
                <option value="lawyer">Lawyer</option>
              </select>
            </div>
            <div className="flex justify-end space-x-3">
              <button onClick={() => setShowInviteModal(false)} className="px-4 py-2 text-sm bg-gray-100 rounded-md" disabled={loading}>
                Cancel
              </button>
              <button onClick={handleSendInvite} disabled={loading} className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-md">
                {loading ? 'Sending...' : 'Add Member'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
